// Outgoing mail: who a reply goes to, subjects, the quoted original, address checks, the size Yahoo accepts, and the
// drafts kept on the device (their text in localStorage, their files from the device in IndexedDB). Plain functions,
// so they can be tested without the DOM.

import { useEffect, useState } from "preact/hooks";
import type { Message } from "./api";
import { API_BASE } from "./auth";
import { sender } from "./format";
import type { ComposeMode } from "./route";
import { db, withRoom } from "./store";

/** Yahoo sends messages of up to 25 MB; attachments grow by a third when encoded (the server checks it exactly). */
export const MAX_MESSAGE_BYTES = 25 * 1024 * 1024;

const ADDRESS = /^[^\s@<>(),;:"[\]]+@[^\s@<>(),;:"[\]]+\.[^\s@<>(),;:"[\]]+$/;

/** The entries of an address field: separated by commas, semicolons or line breaks. */
export function parseAddresses(text: string): string[] {
  return text
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** The address of an entry: `Name <a@b.dk>` or `a@b.dk`. */
export function addressOf(entry: string): string {
  const m = /<([^<>]*)>\s*$/.exec(entry);
  return (m ? m[1] : entry).trim();
}

export function invalidAddresses(entries: string[]): string[] {
  return entries.filter((e) => !ADDRESS.test(addressOf(e)));
}

function unique(addresses: (string | null | undefined)[], leaveOut: string[]): string[] {
  const seen = new Set(leaveOut.map((a) => a.toLowerCase()));
  const out: string[] = [];
  for (const a of addresses) {
    const key = a?.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(a!.trim());
  }
  return out;
}

/** To and Cc of a reply: the sender (its Reply-To when it has one); for "all" also everyone else on the message but
 * me. A reply to my own message goes to its recipients again. */
export function replyRecipients(msg: Message, mode: "reply" | "all", me: string | null): { to: string[]; cc: string[] } {
  const mine = !!me && msg.from_addr?.toLowerCase() === me.toLowerCase();
  const self = me ? [me] : [];
  const original = msg.to ?? [];
  const answer = mine ? original : msg.reply_to?.length ? msg.reply_to : [msg.from_addr];
  const to = unique(mode === "all" && !mine ? [...answer, ...original] : answer, mine ? [] : self);
  const cc = mode === "all" ? unique(msg.cc ?? [], [...self, ...to]) : [];
  return { to: to.length ? to : unique(answer, []), cc };
}

/** Whether "reply all" reaches anyone "reply" does not. */
export function hasMoreRecipients(msg: Message, me: string | null): boolean {
  const one = replyRecipients(msg, "reply", me);
  const all = replyRecipients(msg, "all", me);
  return all.to.length + all.cc.length > one.to.length + one.cc.length;
}

export function prefixedSubject(prefix: "Re" | "Fwd", subject: string | null): string {
  const s = (subject ?? "").trim();
  const known = prefix === "Re" ? /^re:/i : /^fwd?:/i;
  return known.test(s) ? s : `${prefix}: ${s}`.trim();
}

function stamp(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function person(msg: Message): string {
  const name = msg.from_name?.trim();
  return name && msg.from_addr ? `${name} <${msg.from_addr}>` : sender(msg.from_name, msg.from_addr);
}

/** The original below a reply, every line quoted with "> ". */
export function quotedReply(msg: Message, when = stamp(msg.date)): string {
  const lines = (msg.body_text ?? "").replace(/\r\n?/g, "\n").trimEnd().split("\n");
  const quoted = lines.map((line) => (line ? `> ${line}` : ">")).join("\n");
  return `\n\n${when ? `On ${when}, ` : ""}${person(msg)} wrote:\n${quoted}\n`;
}

/** The original below a forward: its headers, then its text. */
export function forwardedText(msg: Message, when = stamp(msg.date)): string {
  const head = [
    "---------- Forwarded message ----------",
    `From: ${person(msg)}`,
    when && `Date: ${when}`,
    `Subject: ${msg.subject ?? ""}`,
    msg.to?.length ? `To: ${msg.to.join(", ")}` : "",
    msg.cc?.length ? `Cc: ${msg.cc.join(", ")}` : "",
  ].filter(Boolean);
  return `\n\n${head.join("\n")}\n\n${(msg.body_text ?? "").replace(/\r\n?/g, "\n").trimEnd()}\n`;
}

/** The fields a reply or forward starts with: recipients, subject and the quoted original; a forward also takes the
 * original's attachments (not the images shown inside its HTML, which a plain-text message cannot show). */
export function startFields(msg: Message, mode: ComposeMode, me: string | null): Pick<Draft, "to" | "cc" | "subject" | "body" | "archived"> {
  if (mode === "forward") {
    const archived = msg.attachments
      .filter((a) => !a.inline)
      .map((a) => ({
        message_id: msg.id,
        part: a.part_index,
        name: a.filename || `attachment-${a.part_index}`,
        size: a.size ?? 0,
        mime: a.mime || "application/octet-stream",
      }));
    return { to: "", cc: "", subject: prefixedSubject("Fwd", msg.subject), body: forwardedText(msg), archived };
  }
  const { to, cc } = replyRecipients(msg, mode === "all" ? "all" : "reply", me);
  return { to: to.join(", "), cc: cc.join(", "), subject: prefixedSubject("Re", msg.subject), body: quotedReply(msg), archived: [] };
}

/** About what the message weighs once encoded, to stop an attachment Yahoo would refuse before it is uploaded. */
export function estimatedSize(body: string, attachmentBytes: number[]): number {
  return Math.ceil(attachmentBytes.reduce((sum, n) => sum + n, 0) * 1.37) + new TextEncoder().encode(body).length + 4096;
}

/** Base64 of a file or blob, for /api/send. */
export function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result);
      resolve(url.slice(url.indexOf(",") + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error("The file could not be read"));
    reader.readAsDataURL(blob);
  });
}

// ---------------------------------------------------------------------------
// the drafts on the device
// ---------------------------------------------------------------------------

/** An attachment of an archived message to send again (forwarding). */
export interface ArchivedAttachment {
  message_id: string;
  part: number;
  name: string;
  size: number;
  mime: string;
}

/** A file from the device attached to the draft: its contents are in IndexedDB under the id (store.ts: draftfiles). */
export interface DraftFile {
  id: string;
  name: string;
}

/** A message being written, kept in localStorage so a reload, a restart of the home screen app (the iPhone ends it in
 * the background) or a sign-in does not lose it, and so it can be left unsent and finished later (Drafts on this
 * device). There can be several, each under its own id (in the URL while it is written: `d`). Its idempotency key goes
 * with it: whatever happens to an answer, the message is sent once. Its files from the device are kept in IndexedDB
 * (keepDraftFiles); `lostFiles` names those that were not (no room, or a draft from before they were kept), to be added
 * again. `touched`: changed since it was started; only such a draft is kept. */
export interface Draft {
  id: string;
  target: string;
  key: string;
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  body: string;
  archived: ArchivedAttachment[];
  files: DraftFile[];
  lostFiles: string[];
  touched: boolean;
  savedAt: number;
}

/** Each draft under a key of its own, so a save writes one draft and two tabs never overwrite each other's. */
const DRAFT_PREFIX = `ym.draft:${API_BASE}:`;
/** The one draft of before there could be several. */
const OLD_DRAFT_KEY = `ym.draft:${API_BASE}`;

/** What a draft belongs to: the kind of message and the message it answers or forwards. */
export function draftTarget(mode: ComposeMode, source: string | null): string {
  return `${mode}:${mode === "new" ? "" : (source ?? "")}`;
}

/** The kind of message a draft is and the message it answers or forwards (its target read back). */
export function draftPlace(draft: Pick<Draft, "target">): { mode: ComposeMode; source: string | null } {
  const at = draft.target.indexOf(":");
  const mode = (["reply", "all", "forward"] as const).find((m) => m === draft.target.slice(0, at)) ?? "new";
  return { mode, source: mode === "new" ? null : draft.target.slice(at + 1) || null };
}

function parsed(raw: string | null): Draft | null {
  if (!raw) return null;
  try {
    const draft = JSON.parse(raw) as Draft;
    return draft && typeof draft.id === "string" ? { ...draft, files: draft.files ?? [], lostFiles: draft.lostFiles ?? [] } : null;
  } catch {
    return null;
  }
}

/** The one draft of an earlier version becomes one of the drafts (when it was written in at all). */
function migrate(): void {
  const raw = localStorage.getItem(OLD_DRAFT_KEY);
  if (raw === null) return;
  try {
    const old = JSON.parse(raw) as Omit<Draft, "id"> | null;
    if (old?.touched) {
      const id = newKey();
      localStorage.setItem(DRAFT_PREFIX + id, JSON.stringify({ ...old, id }));
    }
    localStorage.removeItem(OLD_DRAFT_KEY);
  } catch {
    // left for the next time
  }
}

/** The drafts on the device, the one saved last first. */
export function storedDrafts(): Draft[] {
  try {
    migrate();
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(DRAFT_PREFIX)) keys.push(key);
    }
    return keys
      .map((key) => parsed(localStorage.getItem(key)))
      .filter((d): d is Draft => d !== null)
      .sort((a, b) => b.savedAt - a.savedAt);
  } catch {
    return [];
  }
}

export function loadDraft(id: string): Draft | null {
  try {
    migrate();
    return parsed(localStorage.getItem(DRAFT_PREFIX + id));
  } catch {
    return null;
  }
}

/** The draft kept for a reply or forward of a message, the one saved last: answering it again goes on with it. */
export function draftFor(target: string): Draft | null {
  return storedDrafts().find((d) => d.target === target) ?? null;
}

const listeners = new Set<() => void>();

/** Saves the draft; false when it could not be (storage full or unavailable: it lives on screen only). */
export function saveDraft(draft: Draft): boolean {
  try {
    localStorage.setItem(DRAFT_PREFIX + draft.id, JSON.stringify(draft));
    return true;
  } catch {
    return false;
  } finally {
    listeners.forEach((fn) => fn());
  }
}

/** The draft is sent, in the outbox or discarded: it leaves the device, its files with it. */
export function clearDraft(id: string): void {
  try {
    localStorage.removeItem(DRAFT_PREFIX + id);
  } catch {
    // nothing kept
  }
  listeners.forEach((fn) => fn());
  void pruneDraftFiles();
}

/** The drafts on the device, kept current as they are saved and deleted, in this tab and in others. */
export function useDrafts(): Draft[] {
  const [drafts, setDrafts] = useState(storedDrafts);
  useEffect(() => {
    const update = () => setDrafts(storedDrafts());
    const onStorage = (e: StorageEvent) => {
      if (e.key === null || e.key.startsWith(DRAFT_PREFIX)) update();
    };
    listeners.add(update);
    addEventListener("storage", onStorage);
    update();
    return () => {
      listeners.delete(update);
      removeEventListener("storage", onStorage);
    };
  }, []);
  return drafts;
}

/** The first line written in a draft, for the list of drafts: not the original quoted below a reply or forwarded. */
export function draftPreview(body: string): string {
  for (const line of body.split("\n")) {
    const text = line.trim();
    if (text.startsWith(">") || text === "---------- Forwarded message ----------" || / wrote:$/.test(text)) break;
    if (text) return text;
  }
  return "";
}

/** The draft's files are written, read and deleted one after another, so a file read is one written before. */
let filing: Promise<unknown> = Promise.resolve();

function inOrder<T>(run: () => Promise<T>): Promise<T> {
  const result = filing.then(run);
  filing = result.catch(() => {});
  return result;
}

/** Keeps files added to the draft on the device, so they are still attached when the app is closed before the message
 * is sent and opened again. Storage that runs out is made from the oldest mail in the copy; StorageFull when there is
 * nothing left to make it from. The draft lists them by their ids (saveDraft). */
export function keepDraftFiles(files: { id: string; file: File }[]): Promise<void> {
  return inOrder(async () => {
    const rows = await Promise.all(files.map(async ({ id, file }) => ({ id, name: file.name, mime: file.type || "application/octet-stream", data: await file.arrayBuffer() })));
    await withRoom("￿", () => db().draftfiles.bulkPut(rows));
  });
}

/** A file taken off the draft. */
export function dropDraftFile(id: string): Promise<void> {
  return inOrder(async () => {
    try {
      await db().draftfiles.delete(id);
    } catch (e) {
      console.warn("draft:", e); // left for pruneDraftFiles
    }
  });
}

/** The draft's files from the device as they were kept, and the names of those that were not (to be added again). */
export async function draftFiles(draft: Draft): Promise<{ files: { id: string; file: File }[]; missing: string[] }> {
  const ids = draft.files.map((f) => f.id);
  const rows = ids.length === 0 ? [] : await inOrder(() => db().draftfiles.bulkGet(ids)).catch((e) => (console.warn("draft:", e), []));
  const files: { id: string; file: File }[] = [];
  const missing: string[] = [];
  draft.files.forEach((f, i) => {
    const row = rows[i];
    if (row) files.push({ id: f.id, file: new File([row.data], row.name, { type: row.mime }) });
    else missing.push(f.name);
  });
  return { files, missing };
}

/** Deletes the files no draft on the device lists: those of a draft sent or discarded, and one added just before the app
 * was closed. */
export function pruneDraftFiles(): Promise<void> {
  return inOrder(async () => {
    try {
      const listed = new Set(storedDrafts().flatMap((d) => d.files.map((f) => f.id)));
      const table = db().draftfiles;
      // the keys only: the files are never read into memory
      await table.bulkDelete((await table.toCollection().primaryKeys()).filter((id) => !listed.has(id)));
    } catch (e) {
      console.warn("draft:", e);
    }
  });
}

export function newKey(): string {
  return typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("");
}
